/**
 * API client for the Influencer Portal
 * 
 * Includes Turnstile verification for login endpoints
 */

// In production, use the deployed Worker URL; in dev, use the local proxy
const API_BASE = import.meta.env.PROD 
  ? 'https://influencer-portal-api.robin-cr-sites.workers.dev/api'
  : '/api';

interface ApiError {
  error: string;
  message?: string;
}

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error((data as ApiError).error || 'Request failed');
    }

    return data as T;
  }

  // ============================================================
  // AUTH ENDPOINTS
  // ============================================================

  /**
   * Login with email, password, and Turnstile token
   * For admin: returns requiresVerification = true if 2FA needed
   * For influencer: returns user directly
   */
  async login(email: string, password: string, turnstileToken: string) {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    });
  }

  /**
   * Verify admin 2FA code
   */
  async verifyAdminCode(email: string, code: string) {
    return this.request<{ user: User }>('/auth/admin/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  async logout() {
    return this.request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request<MeResponse>('/auth/me');
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ success: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async setPassword(token: string, password: string) {
    return this.request<{ success: boolean; user: User }>('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ success: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  /**
   * Accept invite and set password (with Turnstile)
   */
  async acceptInvite(
    token: string,
    password: string,
    confirmPassword: string,
    turnstileToken: string
  ) {
    return this.request<{ success: boolean; user: User }>('/auth/accept-invite', {
      method: 'POST',
      body: JSON.stringify({ token, password, confirmPassword, turnstileToken }),
    });
  }

  // ============================================================
  // ADMIN - INFLUENCER MANAGEMENT
  // ============================================================

  async getInfluencers() {
    return this.request<{ influencers: InfluencerWithStatus[] }>('/admin/influencers');
  }

  async getInfluencer(id: number) {
    return this.request<{ influencer: Influencer; user: InfluencerUser | null }>(
      `/admin/influencers/${id}`
    );
  }

  async createInfluencer(data: CreateInfluencerData) {
    return this.request<{ influencer: Influencer }>('/admin/influencers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInfluencer(id: number, data: UpdateInfluencerData) {
    return this.request<{ influencer: Influencer }>(`/admin/influencers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInfluencer(id: number) {
    return this.request<{ success: boolean; message: string }>(`/admin/influencers/${id}`, {
      method: 'DELETE',
    });
  }

  async createInfluencerLogin(id: number, email: string) {
    return this.request<{ user: { id: number; email: string }; inviteUrl: string; expiresAt: string }>(
      `/admin/influencers/${id}/create-login`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async resetInfluencerPassword(id: number) {
    return this.request<{ success: boolean; message: string; resetUrl?: string }>(
      `/admin/influencers/${id}/reset-password`,
      { method: 'POST' }
    );
  }

  /**
   * Send invite email to influencer
   */
  async sendInfluencerInvite(id: number, email?: string) {
    return this.request<SendInviteResponse>(
      `/admin/influencers/${id}/send-invite`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  /**
   * Update influencer's user email
   */
  async updateInfluencerEmail(id: number, email: string) {
    return this.request<{ success: boolean; message: string; email: string }>(
      `/admin/influencers/${id}/update-email`,
      {
        method: 'PATCH',
        body: JSON.stringify({ email }),
      }
    );
  }

  // Legacy: getStorefronts (maps to influencers)
  async getStorefronts() {
    return this.request<{ storefronts: StorefrontWithStats[] }>('/admin/storefronts');
  }

  // ============================================================
  // ADMIN - ORDERS REVIEW
  // ============================================================

  async getOrdersForReview(limit = 50, cursor?: number) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', String(cursor));
    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/admin/orders/review?${params.toString()}`
    );
  }

  // ============================================================
  // INFLUENCER ENDPOINTS
  // ============================================================

  async getInfluencerProfile() {
    return this.request<InfluencerProfile>('/influencer/profile');
  }

  async getInfluencerOrders(params: { range?: MetricsRange; limit?: number; cursor?: number }) {
    const searchParams = new URLSearchParams();
    if (params.range) searchParams.set('range', params.range);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', String(params.cursor));

    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/influencer/orders?${searchParams.toString()}`
    );
  }

  async getInfluencerMetrics(range: MetricsRange = '30d') {
    return this.request<MetricsSummary>(`/influencer/metrics/summary?range=${range}`);
  }

  async getInfluencerMetricsByState(range: MetricsRange = '30d') {
    return this.request<{ states: StateMetrics[]; range: string }>(
      `/influencer/metrics/by-state?range=${range}`
    );
  }

  /**
   * Get single order detail for influencer
   */
  async getInfluencerOrderDetail(shopifyOrderId: string) {
    return this.request<{ order: OrderDetail }>(
      `/influencer/orders/${encodeURIComponent(shopifyOrderId)}`
    );
  }

  // ============================================================
  // ADMIN - VIEW AS INFLUENCER ENDPOINTS
  // ============================================================

  /**
   * Get orders for a specific influencer (admin view)
   */
  async getAdminInfluencerOrders(
    influencerId: number,
    params: { range?: MetricsRange; limit?: number; cursor?: number }
  ) {
    const searchParams = new URLSearchParams();
    if (params.range) searchParams.set('range', params.range);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', String(params.cursor));

    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/admin/influencers/${influencerId}/orders?${searchParams.toString()}`
    );
  }

  /**
   * Get single order detail for a specific influencer (admin view)
   */
  async getAdminInfluencerOrderDetail(influencerId: number, shopifyOrderId: string) {
    return this.request<{ order: OrderDetail }>(
      `/admin/influencers/${influencerId}/orders/${encodeURIComponent(shopifyOrderId)}`
    );
  }

  /**
   * Get metrics summary for a specific influencer (admin view)
   */
  async getAdminInfluencerMetrics(influencerId: number, range: MetricsRange = '30d') {
    return this.request<MetricsSummary>(
      `/admin/influencers/${influencerId}/metrics/summary?range=${range}`
    );
  }

  /**
   * Get orders by state for a specific influencer (admin view)
   */
  async getAdminInfluencerMetricsByState(influencerId: number, range: MetricsRange = '30d') {
    return this.request<{ states: StateMetrics[]; range: string }>(
      `/admin/influencers/${influencerId}/metrics/by-state?range=${range}`
    );
  }

  // ============================================================
  // ADMIN - VENDORS
  // ============================================================

  async getVendors() {
    return this.request<{ vendors: Vendor[] }>('/admin/vendors');
  }

  async getVendor(id: number) {
    return this.request<{ vendor: Vendor }>(`/admin/vendors/${id}`);
  }

  async createVendor(data: CreateVendorData) {
    return this.request<{ vendor: Vendor }>('/admin/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVendor(id: number, data: UpdateVendorData) {
    return this.request<{ vendor: Vendor }>(`/admin/vendors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ADMIN - CATALOG PRODUCTS
  // ============================================================

  async getCatalogProducts(filters?: CatalogProductFilters) {
    const params = new URLSearchParams();
    if (filters?.vendor_id_fk) params.set('vendor_id_fk', String(filters.vendor_id_fk));
    if (filters?.category) params.set('category', filters.category);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.customizable !== undefined) params.set('customizable', String(filters.customizable));
    if (filters?.printing_available !== undefined) params.set('printing_available', String(filters.printing_available));
    if (filters?.embroidery_available !== undefined) params.set('embroidery_available', String(filters.embroidery_available));
    
    return this.request<{ products: CatalogProductWithVendor[] }>(
      `/admin/catalog-products?${params.toString()}`
    );
  }

  async getCatalogProduct(id: number) {
    return this.request<{ product: CatalogProductWithImages }>(
      `/admin/catalog-products/${id}`
    );
  }

  async createCatalogProduct(data: CreateCatalogProductData) {
    return this.request<{ product: CatalogProduct }>('/admin/catalog-products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCatalogProduct(id: number, data: UpdateCatalogProductData) {
    return this.request<{ product: CatalogProduct }>(`/admin/catalog-products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async addCatalogProductImage(productId: number, data: { image_url: string; alt_text?: string; sort_order?: number }) {
    return this.request<{ image: CatalogImage }>(`/admin/catalog-products/${productId}/images`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCatalogProductImage(productId: number, imageId: number) {
    return this.request<{ success: boolean }>(`/admin/catalog-products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // ADMIN - PRODUCT REQUESTS
  // ============================================================

  async getProductRequests(status?: ProductRequestStatus) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return this.request<{ requests: ProductRequestWithDetails[] }>(
      `/admin/product-requests?${params.toString()}`
    );
  }

  async getProductRequest(id: number) {
    return this.request<{ request: ProductRequestWithDetails }>(
      `/admin/product-requests/${id}`
    );
  }

  async updateProductRequestStatus(id: number, status: ProductRequestStatus, adminNotes?: string) {
    return this.request<{ request: ProductRequest }>(`/admin/product-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  }

  // ============================================================
  // ADMIN - SHOPIFY PRODUCTS
  // ============================================================

  async getUnlinkedShopifyProducts() {
    return this.request<{ products: ShopifyProduct[] }>('/admin/shopify-products/unlinked');
  }

  async manualLinkShopifyProduct(data: { shopify_product_id: string; influencer_id_fk?: number; vendor_catalog_product_id_fk?: number }) {
    return this.request<{ product: ShopifyProduct }>('/admin/shopify-products/manual-link', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ADMIN - ANALYTICS
  // ============================================================

  async getTopShopifyProducts(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ products: TopShopifyProduct[]; range: string }>(
      `/admin/analytics/top-shopify-products?range=${range}&limit=${limit}`
    );
  }

  async getTopVendorCatalogProducts(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ products: TopCatalogProduct[]; range: string }>(
      `/admin/analytics/top-vendor-catalog-products?range=${range}&limit=${limit}`
    );
  }

  async getTopInfluencers(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ influencers: TopInfluencer[]; range: string }>(
      `/admin/analytics/top-influencers?range=${range}&limit=${limit}`
    );
  }

  // ============================================================
  // INFLUENCER - CATALOG BROWSING
  // ============================================================

  async browseCatalogProducts(filters?: InfluencerCatalogFilters) {
    const params = new URLSearchParams();
    if (filters?.vendor_id_fk) params.set('vendor_id_fk', String(filters.vendor_id_fk));
    if (filters?.category) params.set('category', filters.category);
    if (filters?.customizable !== undefined) params.set('customizable', String(filters.customizable));
    if (filters?.printing_available !== undefined) params.set('printing_available', String(filters.printing_available));
    if (filters?.embroidery_available !== undefined) params.set('embroidery_available', String(filters.embroidery_available));
    
    return this.request<{ products: InfluencerCatalogProduct[] }>(
      `/influencer/catalog-products?${params.toString()}`
    );
  }

  async getCatalogProductDetail(id: number) {
    return this.request<{ product: InfluencerCatalogProductDetail }>(
      `/influencer/catalog-products/${id}`
    );
  }

  // ============================================================
  // INFLUENCER - PRODUCT REQUESTS
  // ============================================================

  async createProductRequest(data: CreateProductRequestData) {
    return this.request<{ request: ProductRequest }>('/influencer/product-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyProductRequests() {
    return this.request<{ requests: ProductRequestWithDetails[] }>('/influencer/product-requests');
  }

  // ============================================================
  // INFLUENCER - LINKED SHOPIFY PRODUCTS
  // ============================================================

  async getMyShopifyProducts() {
    return this.request<{ products: ShopifyProduct[] }>('/influencer/shopify-products');
  }

  async getProductMetrics(range: MetricsRange = '30d') {
    return this.request<{ products: ProductMetric[]; range: string }>(
      `/influencer/metrics/products?range=${range}`
    );
  }
}

// ============================================================
// TYPES
// ============================================================

export interface User {
  id: number;
  email: string;
  role: 'admin' | 'influencer';
  storefront_id?: number;
}

export interface Influencer {
  id: number;
  first_name: string;
  last_name: string;
  stage_name: string;
  email: string | null;
  storefront_id: number;
  influencer_id: string;
  slug: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface InfluencerWithStatus extends Influencer {
  has_user: boolean;
  has_password: boolean;
  user_id: number | null;
  user_email: string | null;
}

export interface InfluencerUser {
  id: number;
  email: string;
  has_password: boolean;
}

export interface InfluencerProfile {
  id: number;
  stage_name: string;
  first_name: string;
  last_name: string;
  storefront_id: number;
  influencer_id: string;
  slug: string;
  status: 'active' | 'inactive';
}

export interface CreateInfluencerData {
  first_name: string;
  last_name: string;
  stage_name: string;
  storefront_id: number;
  influencer_id: string;
  slug: string;
  email?: string;
  status?: 'active' | 'inactive';
}

export interface UpdateInfluencerData {
  first_name?: string;
  last_name?: string;
  stage_name?: string;
  email?: string;
  status?: 'active' | 'inactive';
}

export interface LoginResponse {
  user?: User;
  requiresVerification?: boolean;
  message?: string;
}

export interface SendInviteResponse {
  success: boolean;
  message: string;
  email: string;
  inviteUrl?: string; // Only in dev mode
  expiresAt: string;
}

export interface MeResponse {
  id: number;
  email: string;
  role: 'admin' | 'influencer';
  storefront_id?: number;
  influencer?: {
    id: number;
    stage_name: string;
    first_name: string;
    last_name: string;
  };
}

// Legacy storefront type (for backward compatibility)
export interface Storefront {
  id: number;
  storefront_id: number;
  influencer_id: string;
  display_name: string;
  storefront_slug: string;
  storefront_domain: string | null;
  creator_email: string;
  tier: string;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface StorefrontWithStats extends Storefront {
  stats: {
    totalOrders: number;
    totalRevenue: number;
  };
}

export interface OrderFact {
  id: number;
  shopify_order_id: string;
  storefront_id: number | null;
  influencer_id: string | null;
  slug: string | null;
  attribution_status: 'verified' | 'unverified' | 'needs_review' | 'unattributed';
  attributed_source: 'note_attributes' | 'metafields' | 'none';
  created_at: string;
  total_price: number;
  currency: string;
  shipping_province: string | null;
  created_at_ingested: string;
  // Privacy-safe customer fields (no street, city, zip, phone, email)
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  order_country?: string | null;
  order_state?: string | null;
}

export interface OrderDetail {
  id: number;
  shopify_order_id: string;
  created_at: string;
  total_price: number;
  currency: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  order_country: string | null;
  order_state: string | null;
  shipping_province: string | null;
  attribution_status: 'verified' | 'unverified' | 'needs_review' | 'unattributed';
  attributed_source: 'note_attributes' | 'metafields' | 'none';
  storefront_id: number | null;
  influencer_id: string | null;
  slug: string | null;
}

export type MetricsRange = '7d' | '30d' | '90d' | 'all';

export interface MetricsSummary {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  currency: string;
  range: string;
}

export interface StateMetrics {
  province: string;
  orderCount: number;
  totalRevenue: number;
}

// ============================================================
// VENDOR & CATALOG TYPES
// ============================================================

export type ShippingCostType = 'flat' | 'per_item' | 'included' | 'quote';

export interface Vendor {
  id: number;
  vendor_id: string;
  name: string;
  status: 'active' | 'inactive';
  ships_for_us: number;
  shipping_origin_country: string | null;
  shipping_origin_state: string | null;
  production_lead_time_days: number | null;
  shipping_lead_time_days: number | null;
  shipping_cost_type: ShippingCostType;
  shipping_cost_flat: number | null;
  contact_email: string | null;
  notes: string | null;
  created_at: string;
}

export interface CreateVendorData {
  vendor_id: string;
  name: string;
  status?: 'active' | 'inactive';
  ships_for_us?: number;
  shipping_origin_country?: string;
  shipping_origin_state?: string;
  production_lead_time_days?: number;
  shipping_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  contact_email?: string;
  notes?: string;
}

export interface UpdateVendorData {
  name?: string;
  status?: 'active' | 'inactive';
  ships_for_us?: number;
  shipping_origin_country?: string;
  shipping_origin_state?: string;
  production_lead_time_days?: number;
  shipping_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  contact_email?: string;
  notes?: string;
}

export interface CatalogProduct {
  id: number;
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  title: string;
  category: string;
  description: string | null;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable: number;
  printing_available: number;
  embroidery_available: number;
  ships_for_us: number | null;
  production_lead_time_days: number | null;
  shipping_cost_type: ShippingCostType | null;
  shipping_cost_flat: number | null;
  moq: number;
  tags: string | null;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface CatalogProductWithVendor extends CatalogProduct {
  vendor_name: string;
}

export interface CatalogImage {
  id: number;
  vendor_catalog_product_id_fk: number;
  image_url: string;
  alt_text: string | null;
  sort_order: number;
  created_at: string;
}

export interface CatalogProductWithImages extends CatalogProductWithVendor {
  images: CatalogImage[];
}

export interface CatalogProductFilters {
  vendor_id_fk?: number;
  category?: string;
  status?: string;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
}

export interface CreateCatalogProductData {
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  title: string;
  category: string;
  description?: string;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
  ships_for_us?: number;
  production_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  moq?: number;
  tags?: string;
  status?: 'active' | 'inactive';
  images?: Array<{ image_url: string; alt_text?: string; sort_order?: number }>;
}

export interface UpdateCatalogProductData {
  title?: string;
  category?: string;
  description?: string;
  base_cost?: number;
  min_retail?: number;
  suggested_retail?: number;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
  ships_for_us?: number;
  production_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  moq?: number;
  tags?: string;
  status?: 'active' | 'inactive';
}

// ============================================================
// PRODUCT REQUEST TYPES
// ============================================================

export type ProductRequestStatus =
  | 'submitted'
  | 'in_review'
  | 'design_in_progress'
  | 'waiting_on_vendor'
  | 'approved_to_create_shopify'
  | 'live'
  | 'rejected'
  | 'cancelled';

export interface ProductRequest {
  id: number;
  influencer_id_fk: number;
  vendor_catalog_product_id_fk: number;
  requested_customization: string | null;
  wants_designer: number;
  design_assets_url: string | null;
  requested_retail: number | null;
  notes: string | null;
  status: ProductRequestStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductRequestWithDetails extends ProductRequest {
  influencer_stage_name: string;
  product_title: string;
  vendor_catalog_product_id?: string;
}

export interface CreateProductRequestData {
  vendor_catalog_product_id_fk: number;
  requested_customization?: string;
  wants_designer?: number;
  design_assets_url?: string;
  requested_retail?: number;
  notes?: string;
}

// ============================================================
// SHOPIFY PRODUCT TYPES
// ============================================================

export type ShopifyProductLinkedBy = 'tags' | 'manual' | 'unlinked';

export interface ShopifyProduct {
  id: number;
  shopify_product_id: string;
  title: string | null;
  status: string | null;
  handle: string | null;
  tags: string | null;
  influencer_id_fk: number | null;
  vendor_catalog_product_id_fk: number | null;
  linked_by: ShopifyProductLinkedBy;
  created_at_shopify: string | null;
  updated_at_shopify: string | null;
  created_at: string;
}

// ============================================================
// ANALYTICS TYPES
// ============================================================

export interface TopShopifyProduct {
  shopify_product_id: string | null;
  title: string | null;
  handle: string | null;
  linked_by: string | null;
  influencer_id_fk: number | null;
  influencer_name: string | null;
  total_units: number;
  total_revenue: number;
  currency: string;
}

export interface TopCatalogProduct {
  vendor_catalog_product_id: number;
  vendor_catalog_product_string_id: string;
  title: string;
  vendor_name: string;
  total_units: number;
  total_revenue: number;
  currency: string;
}

export interface TopInfluencer {
  influencer_id: number;
  stage_name: string;
  first_name: string;
  last_name: string;
  storefront_id: number;
  total_orders: number;
  total_revenue: number;
  currency: string;
}

export interface ProductMetric {
  shopify_product_id: string | null;
  title: string | null;
  total_units: number;
  total_revenue: number;
  currency: string;
}

// ============================================================
// INFLUENCER CATALOG TYPES
// ============================================================

export interface InfluencerCatalogFilters {
  vendor_id_fk?: number;
  category?: string;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
}

export interface InfluencerCatalogProduct {
  id: number;
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  vendor_name: string;
  title: string;
  category: string;
  description: string | null;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable: number;
  printing_available: number;
  embroidery_available: number;
  moq: number;
  tags: string | null;
}

export interface InfluencerCatalogProductDetail extends InfluencerCatalogProduct {
  images: CatalogImage[];
}

export const api = new ApiClient();
